import type { TemplateField, SarsScoresUpdateRequest } from "@/api/researchCms.service";
import { filterByTab, filterIndividualFields, filterOrgFields } from "./filterFieldsByContext";
import type { OrgFormValues } from "../schemas/orgViewSchemas";
import type { RecordedSarsScores } from "./orgDetailMapper";

export const SARS_SCORE_KEYS = [
  "qualityOfWork",
  "clientBase",
  "growthEvidence",
  "aboutUsValue",
  "individualListings",
  "barristerInformation",
  "workHighlightsRelevance",
  "setInformation",
  "clerksInformation",
  "overallSubmission",
] as const;

export type SarsScoreKey = string;

export type SarsGrain = "organisation" | "individual";

export type SarsSlider = {
  scoreKey: SarsScoreKey;
  fieldName: string;
  label: string;
  max: 5 | 10;
};

const SLIDER_MAX_BY_FIELD_TYPE: Record<string, 5 | 10> = {
  slider_5: 5,
  slider_10: 10,
};

export function selectSarsSliders(fields: TemplateField[], grain: SarsGrain): SarsSlider[] {
  const submissionFields = filterByTab(fields, "submission");
  const grainFields =
    grain === "organisation"
      ? filterOrgFields(submissionFields)
      : filterIndividualFields(submissionFields);

  const candidates = grainFields
    .filter(field => field.sarsScoreKey !== null && field.sarsScoreKey !== undefined)
    .slice()
    .sort((a, b) => a.position - b.position)
    .flatMap(field => {
      const max = SLIDER_MAX_BY_FIELD_TYPE[field.fieldType];
      if (!max) {
        return [];
      }

      return [
        {
          scoreKey: field.sarsScoreKey!,
          fieldName: field.fieldName,
          label: field.fieldLabel,
          max,
        },
      ];
    });

  return collapseToWidestOverall(candidates);
}

// bar and litigation_funding seed both an overall_submission_quality and a legacy
// sar_overall_rating that share the overallSubmission score key. Only one control can answer
// that key, so the wider-ranged field wins and the narrower one is dropped. When both share the
// same range (now that both are 0-10), the tie-break keeps the field the template lists last —
// the sliders arrive sorted by position, so on a tie the later element in the array wins.
function collapseToWidestOverall(sliders: SarsSlider[]): SarsSlider[] {
  const overallSliders = sliders.filter(slider => slider.scoreKey === "overallSubmission");
  if (overallSliders.length < 2) {
    return sliders;
  }

  const widestOverall = overallSliders.reduce((widest, slider) =>
    slider.max >= widest.max ? slider : widest,
  );

  return sliders.filter(
    slider => slider.scoreKey !== "overallSubmission" || slider === widestOverall,
  );
}

// Expresses the mean of the component scores as a proportion of the component range, applied to
// the overall's own range — so a 0-5 overall behaves exactly as a plain mean (the factor is 1)
// while a 0-10 overall scales up. Each unanswered score counts as zero in that mean rather than
// being excluded from it, and no answers at all means no overall.
export function calculateOverall(
  scores: (number | null)[],
  componentMax: number,
  overallMax: number,
): number | null {
  const answered = scores.filter((score): score is number => typeof score === "number");

  if (answered.length === 0) {
    return null;
  }

  const mean = answered.reduce((sum, score) => sum + score, 0) / scores.length;
  return Math.floor((mean / componentMax) * overallMax + 0.5);
}

export type SarsWritePlan =
  | { kind: "complete"; body: SarsScoresUpdateRequest }
  | { kind: "partial"; body: SarsScoresUpdateRequest; missingLabels: string[] }
  | { kind: "none" };

// research-api accepts a part-scored body: every key the template seeds, the answered ones with
// their values and the rest as zero — the same thing the card's own average already counts an
// unanswered question as. Deciding here — against the same sliders the card renders — means the
// write and the card can never disagree about which questions exist.
export function planSarsWrite(
  sarScores: OrgFormValues["sarScores"],
  sliders: SarsSlider[],
): SarsWritePlan {
  // An empty slider set withholds the card entirely — there is nothing on screen the
  // researcher could have answered, so there is nothing valid to write either.
  if (sliders.length === 0) return { kind: "none" };
  const unanswered = sliders.filter(slider => sarScores[slider.scoreKey] == null);
  if (unanswered.length === sliders.length) return { kind: "none" };
  const body = Object.fromEntries(
    sliders.map(slider => [slider.scoreKey, sarScores[slider.scoreKey] ?? 0]),
  ) as SarsScoresUpdateRequest;
  if (unanswered.length > 0) {
    return { kind: "partial", body, missingLabels: unanswered.map(slider => slider.label) };
  }
  return { kind: "complete", body };
}

// A newly created row may have just been adopted from a conflict, so its own recorded scores
// fill in whatever this save's form left unanswered rather than being silently overwritten.
export function mergeSarScores(
  formScores: Record<string, number | null>,
  recorded: RecordedSarsScores | null,
): Record<string, number | null> {
  if (!recorded) return formScores;
  const merged = { ...formScores };
  for (const key of Object.keys(merged)) {
    if (merged[key] !== null) continue;
    const recordedValue = recorded[key];
    if (typeof recordedValue === "number") merged[key] = recordedValue;
  }
  return merged;
}
